import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { useTranslation } from 'react-i18next';

import { buttonIcon } from '../../theme';
import styles from './style';

interface SideDrawerProps {
  children: React.ReactNode;
  footer?: React.ReactNode;
  onBack?: () => void;
  onClose: () => void;
  // Scroll of the body, for a list that loads its next page.
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  open: boolean;
  // Over another drawer: no second dark backdrop.
  stacked?: boolean;
  title: React.ReactNode;
  // The title is a person (avatar, name, date): no line under the header, the body starts right after.
  plainHeader?: boolean;
}

const SideDrawer: React.FC<SideDrawerProps> = React.memo((props) => {
  const {
    children,
    footer,
    onBack,
    onClose,
    onScroll,
    open,
    stacked,
    title,
    plainHeader,
  } = props;
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={styles.paperProps}
      BackdropProps={stacked ? styles.backdropPropsStacked : styles.backdropProps}
      transitionDuration={styles.transitionDuration}
    >
      <Box sx={plainHeader ? styles.headerPlain : styles.header}>
        <Box sx={styles.titleRow}>
          {onBack && (
            <IconButton onClick={onBack} aria-label={t('admin.common.back')} sx={buttonIcon}>
              <ChevronLeft />
            </IconButton>
          )}
          {plainHeader ? title : <Typography component="h2" sx={styles.title}>{title}</Typography>}
        </Box>
        <IconButton onClick={onClose} aria-label={t('admin.common.close')} sx={buttonIcon}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={plainHeader ? styles.bodyPlain : styles.body} onScroll={onScroll}>
        {children}
      </Box>
      {footer && (
        <Box sx={styles.footer}>
          {footer}
        </Box>
      )}
    </Drawer>
  );
});

export default SideDrawer;
